// Self-contained base client for the AIAgent/AIExtension "Go" web-request contract (GoWebRequest /
// GoWebStandardListRequest / GoWebStandardSearchRequest / InsertWebRequest / UpdateWebRequest /
// DeleteWebRequest, all wrapped in { metadata, data|...spread, id? } and PascalCase on the wire —
// confirmed by reading flow-studio-api's baseApiClient.ts directly, the actual working client
// behind agentApiClient.ts/credentialApiClient.ts today).
//
// Deliberately NOT importing flow-studio-api's BaseApiClient: that class pulls in
// @atlas-forms/form-actions (EnvelopeStrategyRegistry/FlowStudioEnvelopeStrategy) and a
// flow-studio-local apiConfig module, both workspace-local dependencies of flow-studio's own
// pnpm workspace (see octopus-admin-spec/architecture.md, "Why the existing API clients can't
// just be imported as-is"). This file inlines the small, real pieces of that logic this package
// actually needs (PascalCase conversion + the flow-studio envelope's extract/wrap rules) so
// octopus-admin (and any other future consumer) can reach the same backend without dragging that
// dependency chain across a workspace boundary — mirrors AiAgentCatalogApiClient's
// host-configurable shape (baseUrl/getAuthToken/tenantId/timeoutMs as config, no assumption about
// which app hosts it).

export interface AdminApiConfig {
  baseUrl: string;
  getAuthToken?: () => string | null | undefined;
  tenantId?: number | string;
  timeoutMs?: number;
}

export interface AdminApiError {
  code: string;
  message: string;
  details?: unknown;
}

export interface AdminApiResult<T> {
  success: boolean;
  data?: T;
  error?: AdminApiError;
  metadata?: {
    page?: number;
    pageSize?: number;
    totalCount?: number;
    totalPages?: number;
  };
}

const DEFAULT_TIMEOUT_MS = 30000;

const toPascalCase = (key: string): string => (key ? key.charAt(0).toUpperCase() + key.slice(1) : key);

/** Recursively convert object keys from camelCase to PascalCase, matching the C# backend's wire convention. */
function convertKeysToPascalCase(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map(convertKeysToPascalCase);
  if (typeof value === 'object' && (value as object).constructor === Object) {
    const converted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>)) {
      converted[toPascalCase(key)] = convertKeysToPascalCase((value as Record<string, unknown>)[key]);
    }
    return converted;
  }
  return value;
}

function createRequestId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Base client every entity-specific admin client (Agent/Credential/Team) extends. Implements the
 * same request/response envelope flow-studio's BaseApiClient does — wrapRequest's nestData=true
 * for Create/Update (POST/PUT), nestData=false for Search/Query (spread at root) — verified
 * against real, working call sites in agentApiClient.ts/credentialApiClient.ts.
 */
export class BaseAdminApiClient {
  protected config: AdminApiConfig;

  constructor(config: AdminApiConfig) {
    this.config = config;
  }

  /** Called every render by AiAgentAdminApiProvider so getAuthToken never goes stale. */
  updateConfig(config: AdminApiConfig): void {
    this.config = config;
  }

  private buildUrl(endpoint: string): string {
    const base = this.config.baseUrl.replace(/\/$/, '');
    return `${base}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  }

  private headers(): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (this.config.tenantId !== undefined) {
      headers['X-Tenant-ID'] = String(this.config.tenantId);
    }
    const token = this.config.getAuthToken?.() ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null);
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  private wrapRequest(data: unknown, nestData: boolean): unknown {
    const metadata = {
      requestId: createRequestId(),
      correlationId: createRequestId(),
      timestamp: new Date().toISOString(),
      version: '1.0',
    };
    const converted = convertKeysToPascalCase(data);
    if (nestData) {
      return { metadata, data: converted };
    }
    if (converted && typeof converted === 'object') {
      return { metadata, ...(converted as Record<string, unknown>) };
    }
    return { metadata, data: converted };
  }

  /** Flow-studio envelope's extract rule: prefer Success/Data (after PascalCase), else the raw payload. */
  private extractEnvelope<T>(raw: unknown): AdminApiResult<T> {
    if (!raw || typeof raw !== 'object') {
      return { success: true, data: raw as T };
    }
    const converted = convertKeysToPascalCase(raw) as Record<string, unknown>;
    const success = converted.Success;
    if (success === false) {
      const errorsField = converted.Errors;
      const errorObj = converted.Error as Record<string, unknown> | undefined;
      const errors = Array.isArray(errorsField) ? errorsField : errorObj ? [errorObj] : [];
      const first = errors[0] as Record<string, unknown> | undefined;
      return {
        success: false,
        error: {
          code: (first?.Code as string) || 'API_ERROR',
          message: (first?.Message as string) || (converted.Message as string) || 'The request failed.',
          details: converted,
        },
      };
    }

    const data = 'Data' in converted ? converted.Data : converted;
    const pageInfo = converted.PageInfo as Record<string, unknown> | undefined;
    const metadata = pageInfo
      ? {
          page: (pageInfo.PageNumber as number) ?? (pageInfo.Page as number),
          pageSize: pageInfo.PageSize as number,
          totalCount: (pageInfo.TotalRecords as number) ?? (pageInfo.TotalCount as number),
          totalPages: pageInfo.TotalPages as number,
        }
      : undefined;

    return { success: true, data: data as T, ...(metadata ? { metadata } : {}) };
  }

  private async request<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', endpoint: string, body: unknown, nestData: boolean): Promise<AdminApiResult<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeoutMs || DEFAULT_TIMEOUT_MS);
    try {
      const wrappedBody = method === 'GET' || body === undefined ? undefined : this.wrapRequest(body, nestData);
      const response = await fetch(this.buildUrl(endpoint), {
        method,
        headers: this.headers(),
        body: wrappedBody ? JSON.stringify(wrappedBody) : undefined,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const contentType = response.headers.get('content-type');
      const isJson = contentType?.includes('application/json') || contentType?.includes('application/problem+json');

      if (!response.ok) {
        let message = `HTTP ${response.status} ${response.statusText}`;
        if (isJson) {
          try {
            const errBody = await response.json();
            message = errBody?.title || errBody?.message || errBody?.Message || message;
          } catch {
            // non-JSON error body, keep the status-line message
          }
        }
        return { success: false, error: { code: `HTTP_${response.status}`, message } };
      }

      if (!isJson) return { success: true, data: undefined };
      const raw = await response.json();
      return this.extractEnvelope<T>(raw);
    } catch (error: unknown) {
      clearTimeout(timeoutId);
      const err = error as { name?: string; message?: string };
      if (err?.name === 'AbortError') {
        return { success: false, error: { code: 'TIMEOUT', message: 'Request timed out.' } };
      }
      return { success: false, error: { code: 'NETWORK_ERROR', message: err?.message || 'Network error.' } };
    }
  }

  /** GET request — used only by the few endpoints that take no body (e.g. lookup lists). */
  protected get<T>(endpoint: string): Promise<AdminApiResult<T>> {
    return this.request<T>('GET', endpoint, undefined, false);
  }

  /** POST for Create — nests the body in a `data` property (InsertWebRequest shape). */
  protected post<T>(endpoint: string, body?: unknown): Promise<AdminApiResult<T>> {
    return this.request<T>('POST', endpoint, body ?? {}, true);
  }

  /** POST for Search/Query — spreads the body at root level alongside metadata. */
  protected postQuery<T>(endpoint: string, body?: unknown): Promise<AdminApiResult<T>> {
    return this.request<T>('POST', endpoint, body ?? {}, false);
  }

  /** PUT for Update — nests the body in a `data` property (UpdateWebRequest shape). */
  protected put<T>(endpoint: string, body?: unknown): Promise<AdminApiResult<T>> {
    return this.request<T>('PUT', endpoint, body ?? {}, true);
  }

  /** DELETE — spreads the body at root level (DeleteWebRequest shape). */
  protected del<T>(endpoint: string, body?: unknown): Promise<AdminApiResult<T>> {
    return this.request<T>('DELETE', endpoint, body ?? {}, false);
  }
}
